import { useLayoutEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import {
  findDatesRange,
  DatesRangeFilterSelector,
  useAnalyticsOrgLocations,
  useTimePeriodDropDownSelector,
  useTimePeriodPopoverSelector,
} from '@frontend/analytics-shared';
import { i18next } from '@frontend/i18n';
import { humanizeDateDifference } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../../constants';
import { useIsAppointmentDemoAccount } from '../../hooks/use-is-appointment-demo-account';
import { trackingIds } from '../../tracking-ids';

interface AppointmentTimePeriodFilterProps {
  activeTab: APPOINTMENT_TABS;
  // We accept both upcomingDates and pastDates to keep input values stable for useTimePeriodPopoverSelector.
  // Passing a single activeStartDate/activeEndDate prop would trigger an internal state re-evaluation in both selectors
  // on tab-switches (as the dates update to the newly active tab), resulting in a brief moment of undefined selectedPeriod,
  // which causes layout flickering of the filterHintText (humanized date range fallback) and the trigger button (turning blue).
  upcomingDates: { startDate?: string; endDate?: string };
  pastDates: { startDate?: string; endDate?: string };
  locationId?: string[];
  setFilterHintText?: (text: string) => void;
  onChange: (dates: { startDate: string; endDate: string }) => void;
  isTrayFilter?: boolean;
}

const UpcomingTimePeriods = () => ({
  next7Days: {
    label: i18next.t('Next 7 Days', { ns: 'analytics' }),
    value: findDatesRange({ offset: 7, range: 'day', type: 'future' }),
  },
  next15Days: {
    label: i18next.t('Next 15 Days', { ns: 'analytics' }),
    value: findDatesRange({ offset: 15, range: 'day', type: 'future' }),
  },
});

const PastTimePeriods = () => ({
  last7Days: {
    label: i18next.t('Last 7 Days', { ns: 'analytics' }),
    value: findDatesRange({ offset: 7, range: 'day', type: 'past' }),
  },
  last30Days: {
    label: i18next.t('Last 30 Days', { ns: 'analytics' }),
    value: findDatesRange({ offset: 30, range: 'day', type: 'past' }),
  },
});

const TimePeriodTrayVariant = ({
  activeTab,
  startDate,
  endDate,
  locationId,
  onChange,
  locationIds,
  preselectedAccessibleLocationIds,
  upcomingTimePeriods,
  pastTimePeriods,
}: any) => {
  const values = useMemo(
    () => ({
      endDate,
      selectedPeriod: undefined,
      startDate,
    }),
    [endDate, startDate]
  );

  const { TimePeriodDropDownSelector } = useTimePeriodDropDownSelector<any>({
    defaultPeriod: activeTab === APPOINTMENT_TABS.UPCOMING ? 'next7Days' : 'last30Days',
    bypassMinDateFilter: true,
    timePeriods: activeTab === APPOINTMENT_TABS.UPCOMING ? upcomingTimePeriods : pastTimePeriods,
    values,
  });

  return (
    <div css={styles.trayWrapper}>
      <Heading level={3} css={styles.heading}>
        {i18next.t('Time Period', { ns: 'analytics' })}
      </Heading>
      <TimePeriodDropDownSelector
        customTimePeriodTracking={{
          filteredLocations: JSON.stringify(locationId ?? []),
          filteredLocationsCount: locationId?.length ?? 0,
          selectedLocationIds: JSON.stringify(locationIds),
          totalLocationsCount: preselectedAccessibleLocationIds?.length ?? 0,
        }}
        onChange={({ startDate: sDate, endDate: eDate }) => {
          onChange({
            startDate: dayjs(sDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
            endDate: dayjs(eDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          });
        }}
        persistDefaultLabel
        trackingIdBase={`${trackingIds.filterTrayToggle}-time-period`}
      />
      <div css={styles.dateSelector}>
        <DatesRangeFilterSelector
          endDate={endDate}
          minDate={
            activeTab === APPOINTMENT_TABS.UPCOMING
              ? dayjs().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS')
              : undefined
          }
          onChange={({ endDate: eDate, startDate: sDate }) => {
            onChange({
              startDate: dayjs(sDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
              endDate: dayjs(eDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
            });
          }}
          startDate={startDate}
        />
      </div>
    </div>
  );
};

const TimePeriodInlineVariant = ({
  activeTab,
  upcomingDates,
  pastDates,
  locationId,
  setFilterHintText,
  onChange,
  locationIds,
  preselectedAccessibleLocationIds,
  upcomingTimePeriods,
  pastTimePeriods,
}: any) => {
  const upcomingValues = useMemo(
    () => ({
      endDate: upcomingDates.endDate,
      selectedPeriod: undefined,
      startDate: upcomingDates.startDate,
    }),
    [upcomingDates.endDate, upcomingDates.startDate]
  );

  const pastValues = useMemo(
    () => ({
      endDate: pastDates.endDate,
      selectedPeriod: undefined,
      startDate: pastDates.startDate,
    }),
    [pastDates.endDate, pastDates.startDate]
  );

  const {
    TimePeriodPopoverSelector: UpcomingAppointmentTimePeriodsSelector,
    selectedPeriod: upcomingSelectedPeriod,
    timePeriods: upcomingCalculatedTimePeriods,
  } = useTimePeriodPopoverSelector<{
    next7Days: { label: string; value: { startDate: string; endDate: string } };
    next15Days: { label: string; value: { startDate: string; endDate: string } };
  }>({
    defaultPeriod: 'next7Days',
    bypassMinDateFilter: true,
    timePeriods: upcomingTimePeriods,
    values: upcomingValues,
  });

  const {
    TimePeriodPopoverSelector: PastAppointmentTimePeriodsSelector,
    selectedPeriod: pastSelectedPeriod,
    timePeriods: pastCalculatedTimePeriods,
  } = useTimePeriodPopoverSelector({
    defaultPeriod: 'last30Days',
    bypassMinDateFilter: true,
    timePeriods: pastTimePeriods,
    values: pastValues,
  });

  const activeStartDate = activeTab === APPOINTMENT_TABS.UPCOMING ? upcomingDates.startDate : pastDates.startDate;
  const activeEndDate = activeTab === APPOINTMENT_TABS.UPCOMING ? upcomingDates.endDate : pastDates.endDate;

  useLayoutEffect(() => {
    if (activeStartDate && activeEndDate && setFilterHintText) {
      const selectedPeriod = activeTab === APPOINTMENT_TABS.UPCOMING ? upcomingSelectedPeriod : pastSelectedPeriod;
      const timePeriods =
        activeTab === APPOINTMENT_TABS.UPCOMING ? upcomingCalculatedTimePeriods : pastCalculatedTimePeriods;

      setFilterHintText(
        selectedPeriod
          ? (timePeriods as any)?.[selectedPeriod]?.label
          : humanizeDateDifference(activeStartDate, activeEndDate)
      );
    }
  }, [
    activeStartDate,
    activeEndDate,
    activeTab,
    pastCalculatedTimePeriods,
    pastSelectedPeriod,
    setFilterHintText,
    upcomingCalculatedTimePeriods,
    upcomingSelectedPeriod,
  ]);

  if (activeTab === APPOINTMENT_TABS.UPCOMING) {
    return (
      <UpcomingAppointmentTimePeriodsSelector
        customTimePeriodTracking={{
          filteredLocations: JSON.stringify(locationId ?? []),
          filteredLocationsCount: locationId?.length ?? 0,
          selectedLocationIds: JSON.stringify(locationIds),
          totalLocationsCount: preselectedAccessibleLocationIds?.length ?? 0,
        }}
        onChange={({ startDate: sDate, endDate: eDate }) => {
          onChange({
            startDate: dayjs(sDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
            endDate: dayjs(eDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          });
        }}
        persistDefaultLabel
        trackingIdBase={`${trackingIds.filtersOpenUpcoming}-time-period`}
        minDate={dayjs().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS')}
        showDateRangePicker={false}
      />
    );
  }

  return (
    <PastAppointmentTimePeriodsSelector
      customTimePeriodTracking={{
        filteredLocations: JSON.stringify(locationId ?? []),
        filteredLocationsCount: locationId?.length ?? 0,
        selectedLocationIds: JSON.stringify(locationIds),
        totalLocationsCount: preselectedAccessibleLocationIds?.length ?? 0,
      }}
      onChange={({ startDate: sDate, endDate: eDate }) => {
        onChange({
          startDate: dayjs(sDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          endDate: dayjs(eDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
        });
      }}
      persistDefaultLabel
      trackingIdBase={`${trackingIds.filtersOpenPast}-time-period`}
    />
  );
};

export const AppointmentTimePeriodFilter = ({
  activeTab,
  upcomingDates,
  pastDates,
  locationId,
  setFilterHintText,
  onChange,
  isTrayFilter,
}: AppointmentTimePeriodFilterProps) => {
  const isDemoAccount = useIsAppointmentDemoAccount();

  const { locationIds, preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'APPOINTMENT',
  });

  const upcomingTimePeriods = useMemo(() => UpcomingTimePeriods(), []);
  const pastTimePeriods = useMemo(() => PastTimePeriods(), []);

  if (isTrayFilter) {
    const activeDates = activeTab === APPOINTMENT_TABS.UPCOMING ? upcomingDates : pastDates;
    return (
      <TimePeriodTrayVariant
        activeTab={activeTab}
        startDate={activeDates.startDate}
        endDate={activeDates.endDate}
        locationId={locationId}
        onChange={onChange}
        locationIds={locationIds}
        preselectedAccessibleLocationIds={preselectedAccessibleLocationIds}
        upcomingTimePeriods={upcomingTimePeriods}
        pastTimePeriods={pastTimePeriods}
      />
    );
  }

  return (
    <TimePeriodInlineVariant
      activeTab={activeTab}
      upcomingDates={upcomingDates}
      pastDates={pastDates}
      locationId={locationId}
      setFilterHintText={setFilterHintText}
      onChange={onChange}
      locationIds={locationIds}
      preselectedAccessibleLocationIds={preselectedAccessibleLocationIds}
      upcomingTimePeriods={upcomingTimePeriods}
      pastTimePeriods={pastTimePeriods}
    />
  );
};

const styles = {
  trayWrapper: css`
    margin-top: ${theme.spacing(3)};
  `,
  heading: css`
    font-size: ${theme.fontSize(16)};
    margin-bottom: ${theme.spacing(2)};
  `,
  dateSelector: css`
    margin-top: ${theme.spacing(2)};
  `,
};
